/**
 * Control Implementation (workbench step 7) — stage metadata, role gating, and helpers.
 *
 * Each "doc" in `controlImplementationPackage.docs` advances through six stages.
 * The transition between stages requires (a) all checklist items for the current stage
 * are checked, and (b) a Lead Advisor / admin approval click.
 */

import { Role } from "../constants/roles";

export const CI_TEMPLATE = "ci-v1";

export const STAGE_ORDER = Object.freeze([
  "drafting",
  "delegate_review",
  "spoc_review",
  "published",
  "training",
  "implemented",
]);

/** Per-stage metadata: label, color, assignee role, ordered checklist items. */
export const STAGE_META = Object.freeze({
  drafting: {
    key: "drafting",
    label: "Drafting",
    pillClass: "bg-blue-50 text-blue-700 border-blue-200",
    assigneeRole: Role.ADVISOR,
    assigneeLabel: "Advisor",
    advanceCtaLabel: "Assign to Delegate",
    items: [
      { key: "update_branding", label: "Update control artefacts with company branding", assignee: "Advisor" },
      { key: "create_training_materials", label: "Create training materials", assignee: "Advisor" },
    ],
  },
  delegate_review: {
    key: "delegate_review",
    label: "Delegate Review",
    pillClass: "bg-indigo-50 text-indigo-700 border-indigo-200",
    assigneeRole: Role.COMPANY_DELEGATE,
    assigneeLabel: "Delegate",
    advanceCtaLabel: "Send to SPOC Review",
    items: [
      { key: "review_advisor_content", label: "Review content assigned by Advisor", assignee: "Delegate" },
      { key: "add_amend", label: "Add / amend not covered by advisor", assignee: "Delegate" },
    ],
  },
  spoc_review: {
    key: "spoc_review",
    label: "SPOC Review",
    pillClass: "bg-amber-50 text-amber-700 border-amber-200",
    assigneeRole: Role.COMPANY_SPOC,
    assigneeLabel: "SPOC",
    advanceCtaLabel: "Approve for Publish",
    items: [
      { key: "review_amend_delegate_content", label: "Review and amend the content submitted by Company Delegate", assignee: "SPOC" },
      { key: "present_for_ratification", label: "Present approved artefacts in the forum for ratification", assignee: "SPOC" },
    ],
  },
  published: {
    key: "published",
    label: "Published",
    pillClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    assigneeRole: Role.COMPANY_SPOC,
    assigneeLabel: "SPOC",
    advanceCtaLabel: "Move to Training",
    items: [
      { key: "publish_artefacts", label: "Publish the artefacts", assignee: "SPOC" },
    ],
  },
  training: {
    key: "training",
    label: "Training",
    pillClass: "bg-sky-50 text-sky-700 border-sky-200",
    assigneeRole: Role.ADVISOR,
    assigneeLabel: "Advisor",
    advanceCtaLabel: "Mark Implemented",
    items: [
      { key: "conduct_training", label: "Conduct training sessions", assignee: "Advisor" },
      { key: "facilitate_training_efforts", label: "Facilitate the training efforts", assignee: "Advisor" },
      { key: "facilitate_collaboration", label: "Facilitate collaboration amongst Advisor, Delegate and SPOC", assignee: "Advisor" },
    ],
  },
  implemented: {
    key: "implemented",
    label: "Implemented",
    pillClass: "bg-violet-50 text-violet-700 border-violet-200",
    assigneeRole: Role.ADVISOR,
    assigneeLabel: "Advisor",
    advanceCtaLabel: null,
    items: [
      { key: "conduct_training_sessions", label: "Conduct training sessions", assignee: "Advisor" },
      { key: "finalize_checklists_logs_dashboards", label: "Finalize the checklists, logs, dashboards etc.", assignee: "Advisor" },
      { key: "push_implementation_status", label: "Push the implementation status to dashboard feeds", assignee: "Advisor" },
    ],
  },
});

export const FAMILY_OPTIONS = Object.freeze(["Organizational", "People", "Physical", "Technological"]);

/** Return the next stage key in the lifecycle, or null if already at the final stage. */
export function getNextStage(stage) {
  const i = STAGE_ORDER.indexOf(stage);
  if (i < 0 || i >= STAGE_ORDER.length - 1) return null;
  return STAGE_ORDER[i + 1];
}

/** Progress percent 0–100 for the lifecycle progress bar. */
export function progressPercentForStage(stage) {
  const i = STAGE_ORDER.indexOf(stage);
  if (i < 0) return 0;
  return Math.round((i / (STAGE_ORDER.length - 1)) * 100);
}

/** Are all checklist items (built-in minus removed + custom) in the given stage checked on the doc? */
export function isStageChecklistComplete(doc, stage) {
  const meta = STAGE_META[stage];
  if (!meta) return false;
  const cl = (doc && doc.checklist && doc.checklist[stage]) || {};
  const removed = Array.isArray(doc?.removedBuiltInItems) ? doc.removedBuiltInItems : [];
  const activeBuiltIn = meta.items.filter((it) => !removed.includes(it.key));
  if (!activeBuiltIn.every((it) => cl[it.key] === true)) return false;
  const custom = (doc?.customChecklistItems || []).filter((c) => c.stage === stage);
  return custom.every((c) => cl[c.key] === true);
}

/** Get all items for a stage: built-in (minus removed) + custom from the doc. */
export function allItemsForStage(doc, stage) {
  const meta = STAGE_META[stage];
  if (!meta) return [];
  const removed = Array.isArray(doc?.removedBuiltInItems) ? doc.removedBuiltInItems : [];
  const builtIn = meta.items.filter((it) => !removed.includes(it.key));
  const custom = (doc?.customChecklistItems || []).filter((c) => c.stage === stage);
  return [
    ...builtIn,
    ...custom.map((c) => ({ key: c.key, label: c.label, assignee: c.assignee || "Advisor", isCustom: true })),
  ];
}

/** Role that owns checklist edits at a given stage. */
export function assigneeRoleForStage(stage) {
  return STAGE_META[stage]?.assigneeRole || null;
}

/** Can the user role tick / untick checklist items on this stage? */
export function canRoleEditStageChecklist(role, stage) {
  if (!role || !stage) return false;
  const owner = assigneeRoleForStage(stage);
  if (role === Role.ADMIN) return true;
  return role === owner;
}

/** Any stage owner (or admin) can advance by submitting — no separate LA gate. */
export function canRoleAdvanceStage(role, stage) {
  if (role === Role.ADMIN) return true;
  const owner = assigneeRoleForStage(stage);
  return role === owner;
}

/** UI-only "phase complete" — used by the main 9-step bar in ProjectWorkspace. */
export function isControlImplementationFinalized(profile) {
  const pkg = profile?.controlImplementationPackage;
  if (!pkg || typeof pkg !== "object") return false;
  const docs = pkg.docs && typeof pkg.docs === "object" ? pkg.docs : {};
  const keys = Object.keys(docs);
  if (keys.length === 0) return false;
  return keys.every((k) => {
    const d = docs[k];
    if (!d || typeof d !== "object") return false;
    if (d.deactivatedAt) return true;
    return d.stage === "implemented";
  });
}

/** Counts per stage for KPI tiles + filter chips. */
export function summarizeStageCounts(docsMap) {
  const docs = docsMap && typeof docsMap === "object" ? docsMap : {};
  const counts = STAGE_ORDER.reduce((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {});
  let total = 0;
  let inProgress = 0;
  let implemented = 0;
  for (const key of Object.keys(docs)) {
    const d = docs[key];
    if (!d || typeof d !== "object" || d.deactivatedAt) continue;
    total += 1;
    const s = STAGE_ORDER.includes(d.stage) ? d.stage : "drafting";
    counts[s] += 1;
    if (s === "implemented") implemented += 1;
    else inProgress += 1;
  }
  return { total, inProgress, implemented, byStage: counts };
}

/** Stable display id for a control row (e.g. `ISMS-DOC-A05-1-1` for control 5.1). */
export function controlDocCode(controlId, sequence = 1) {
  const parts = String(controlId || "").split(".");
  if (parts.length !== 2) return `ISMS-DOC-${controlId}`;
  const major = parts[0].padStart(2, "0");
  return `ISMS-DOC-A${major}-${parts[1]}-${sequence}`;
}

/**
 * Empty checklist scaffold for a fresh doc. Drafting/`update_branding` is auto-checked because
 * Organizational Setup already applied branding to artefacts in step 5.
 */
export function defaultChecklistForNewDoc({ brandingApplied = false } = {}) {
  return {
    drafting: { update_branding: brandingApplied === true, create_training_materials: false },
    delegate_review: {},
    spoc_review: {},
    published: {},
    training: {},
    implemented: {},
  };
}
